"use client";

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { getJson, apiPost } from '@/lib/api';
import { track } from '@/lib/analytics';
import { trackEvent } from '@/lib/tracking';

type CommentDto = {
  id: number | string;
  author?: string;
  text: string;
  createdAt?: string; // ISO
};

function isArray<T>(x: any): x is T[] {
  return Array.isArray(x);
}

export default function CommentsPanelClient({ id, resource = 'video' }: { id: string | number; resource?: 'video' | 'post' | 'gallery' }) {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const [comments, setComments] = useState<CommentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);

  const base = resource === 'video'
    ? `/api/video/v2/${encodeURIComponent(String(id))}`
    : resource === 'post'
      ? `/api/post/${encodeURIComponent(String(id))}`
      : `/api/gallery/${encodeURIComponent(String(id))}`;
  const listPath = `${base}/comments?skip=0&take=20`;
  const postPath = `${base}/comments`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const json: any = await getJson(listPath);
        const arr = isArray<CommentDto>(json)
          ? json
          : (isArray<CommentDto>(json?.items) ? json.items : []);
        if (!cancelled) setComments(arr);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load comments');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [listPath, resource]);

  const canPost = status === 'authenticated';
  const remaining = 2000 - text.length;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canPost || posting) return;
    let body = (text || '').replace(/\s+/g, ' ').trim();
    if (!body) return;
    if (body.length > 2000) body = body.slice(0, 2000);

    setPosting(true);
    try {
      const saved = await apiPost<CommentDto>(postPath, session, { text: body });
      setComments((prev) => [saved, ...prev]);
      setText('');
      try { track('comment_posted', { resource, id: String(id) }); } catch {}
      try { trackEvent('comment_posted', { resource_type: resource, resource_id: String(id) }); } catch {}
    } catch (e: any) {
      setError(e?.message || 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="mt-6 space-y-3" aria-busy={loading ? 'true' : 'false'}>
      <h2 className="text-lg font-semibold">Comments</h2>

      {/* Form */}
      {canPost ? (
        <form onSubmit={submit} className="space-y-1">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 2000))}
            className="w-full min-h-20 rounded border p-2 bg-background text-foreground"
            placeholder="Write a comment."
          />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{remaining} characters left</span>
            <button
              type="submit"
              disabled={posting || text.trim().length === 0}
              className="px-3 py-1 rounded border"
            >
              {posting ? 'Posting…' : 'Post'}
            </button>
          </div>
        </form>
      ) : (
        // Unified guest gate: register with returnTo (same destination as
        // Chat/Email/Watch), not the old signIn() bounce to a login form.
        <div className="rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          <Link
            href={`/register?returnTo=${encodeURIComponent(pathname || '/')}`}
            className="font-semibold text-[hsl(var(--primary))] hover:underline"
          >
            Join free
          </Link>
          {' '}to comment and message the poster directly.
        </div>
      )}

      {/* Skeleton / Errors / List */}
      {loading ? (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-12 rounded bg-muted animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div className="text-sm text-yellow-700">{error}</div>
      ) : comments.length === 0 ? (
        <div className="text-sm text-muted-foreground">No comments yet.</div>
      ) : (
        <ul className="space-y-3" role="region" aria-live="polite">
          {comments.map((c) => (
            <li key={String(c.id)} className="border rounded p-2">
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <span>{c.author || 'User'}</span>
                {c.createdAt && (
                  <span>{new Date(c.createdAt).toLocaleString()}</span>
                )}
              </div>
              <div className="text-sm whitespace-pre-wrap">{c.text}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
